// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/OrcamentoForm.tsx
//  Página de lançamento / edição de Orçamento — inspirada no frm_orcamento.cs
// ════════════════════════════════════════════════════════════════════════════

import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Package, CreditCard,
  User, ClipboardList, ArrowRightCircle,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { orcamentoService }              from '@/features/operacional/services/orcamentoService';
import { davService }                    from '@/features/operacional/services/davService';
import { useFormasPagamentoCombo, useCondicoesPagamentoCombo } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { PessoaSearchCombobox }          from '@/components/PessoaSearchCombobox';
import { VendedorSearchCombobox }        from '@/components/VendedorSearchCombobox';
import { ProdutoNome }                   from '@/components/ProdutoNome';
import { getApiErrorMessage }            from '@/lib/apiClient';
import { useAuth }                       from '@/hooks/useAuth';

import { Button }                        from '@/components/ui/button';
import { Input }                         from '@/components/ui/input';
import { Label }                         from '@/components/ui/label';
import { Textarea }                      from '@/components/ui/textarea';
import { Badge }                         from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

import type {
  CriarOrcamentoRequest, OrcamentoProdutoRequest, AtualizarOrcamentoRequest,
  OrcamentoParcelaRequest,
} from '@/features/operacional/types';

// Parcela em edição na aba Financeiro.
interface ParcelaPagamento {
  numero: number;
  formaPagRecId: number | '';
  dtVencimento: string;
  valor: number;
}

// ── Tipos internos ────────────────────────────────────────────────────────

interface ItemProduto {
  key: number;
  produtoId: number | '';
  termoBusca: string;
  qtdMovimento: number;
  valorUnitario: number;
  valorDesconto: number;
  valorAcrescimo: number;
  observacao: string;
}

const ITEM_VAZIO = (key: number): ItemProduto => ({
  key,
  produtoId: '',
  termoBusca: '',
  qtdMovimento: 1,
  valorUnitario: 0,
  valorDesconto: 0,
  valorAcrescimo: 0,
  observacao: '',
});

let _keySeq = 1;
const nextKey = () => ++_keySeq;

// ── Componente: linha de produto ──────────────────────────────────────────

interface ProdutoLinhaProps {
  item: ItemProduto;
  onChange: (campo: keyof ItemProduto, valor: string | number) => void;
  onRemove: () => void;
  showRemove: boolean;
}

function ProdutoLinha({ item, onChange, onRemove, showRemove }: ProdutoLinhaProps) {

  const subtotal =
    item.qtdMovimento * item.valorUnitario - item.valorDesconto + item.valorAcrescimo;

  return (
    <TableRow>
      {/* Produto */}
      <TableCell className="min-w-[220px]">
        <ProdutoSearchCombobox
          value={item.produtoId === '' ? undefined : item.produtoId}
          descricaoInicial={item.termoBusca || null}
          mostrarPreco
          placeholder="Buscar produto (código, nome ou EAN)..."
          onChange={() => {}}
          onSelectProduto={(p) => {
            onChange('produtoId', p.id);
            onChange('termoBusca', p.descricao);
            onChange('valorUnitario', p.precoPadrao ?? 0);
          }}
        />
      </TableCell>
      {/* Qtd */}
      <TableCell className="w-20">
        <Input
          type="number" min={0.001} step={0.001}
          value={item.qtdMovimento}
          onChange={(e) => onChange('qtdMovimento', Number(e.target.value))}
          className="text-right"
        />
      </TableCell>
      {/* Vlr Unit */}
      <TableCell className="w-28">
        <Input
          type="number" min={0} step={0.01}
          value={item.valorUnitario}
          onChange={(e) => onChange('valorUnitario', Number(e.target.value))}
          className="text-right"
        />
      </TableCell>
      {/* Desconto */}
      <TableCell className="w-24">
        <Input
          type="number" min={0} step={0.01}
          value={item.valorDesconto}
          onChange={(e) => onChange('valorDesconto', Number(e.target.value))}
          className="text-right"
        />
      </TableCell>
      {/* Acréscimo */}
      <TableCell className="w-24">
        <Input
          type="number" min={0} step={0.01}
          value={item.valorAcrescimo}
          onChange={(e) => onChange('valorAcrescimo', Number(e.target.value))}
          className="text-right"
        />
      </TableCell>
      {/* Observação do item */}
      <TableCell className="min-w-[160px]">
        <Input
          value={item.observacao}
          placeholder="Observação..."
          onChange={(e) => onChange('observacao', e.target.value)}
        />
      </TableCell>
      {/* Subtotal */}
      <TableCell className="text-right font-medium w-28">
        {subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
      </TableCell>
      {/* Remover */}
      <TableCell className="w-10">
        {showRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

const OrcamentoForm = () => {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id: string }>();
  const isEditing = !!id;
  const { user }  = useAuth();
  const queryClient = useQueryClient();

  // ── Estado do formulário ───────────────────────────────────────────────

  const [vendedorId,   setVendedorId]   = useState<number | null>(null);
  const [clienteId,    setClienteId]    = useState<number | null>(null);
  const [nomeRazao,    setNomeRazao]    = useState('');
  const [cpfCnpj,      setCpfCnpj]      = useState('');
  const [fone,         setFone]         = useState('');
  const [celular,      setCelular]      = useState('');
  const [dtEmissao,    setDtEmissao]    = useState(() => new Date().toISOString().slice(0, 10));
  const [dtValidade,   setDtValidade]   = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [dtPrevisao,   setDtPrevisao]   = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [observacao,   setObservacao]   = useState('');
  const [itens,        setItens]        = useState<ItemProduto[]>([ITEM_VAZIO(nextKey())]);

  // ── Estado: Financeiro (condições de pagamento) ────────────────────────
  const [formaPagRecId,      setFormaPagRecId]      = useState<number | ''>('');
  const [condicaoPagamentoId, setCondicaoPagamentoId] = useState<number | ''>('');
  const [qtdParcelas,        setQtdParcelas]        = useState(1);
  const [diasParcela,        setDiasParcela]        = useState(30);
  const [dtPrimeiroVenc,     setDtPrimeiroVenc]     = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [parcelas,           setParcelas]           = useState<ParcelaPagamento[]>([]);

  const { formasPagamento }   = useFormasPagamentoCombo();
  const { condicoes: condicoesPagamento } = useCondicoesPagamentoCombo();

  // ── Pré-carga em modo edição ───────────────────────────────────────────

  const { data: orcamento } = useQuery({
    queryKey: ['orcamento-detalhe', id],
    queryFn: () => orcamentoService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (!orcamento) return;
    setVendedorId(orcamento.vendedorId);
    setClienteId(orcamento.clienteId ?? null);
    setNomeRazao(orcamento.nomeRazao ?? '');
    setCpfCnpj(orcamento.cpfCnpj ?? '');
    setFone(orcamento.fone ?? '');
    setCelular(orcamento.celular ?? '');
    setDtEmissao(orcamento.dtEmissao.slice(0, 10));
    setDtValidade(orcamento.dtValidade.slice(0, 10));
    setDtPrevisao(orcamento.dtPrevisao?.slice(0, 10) ?? '');
    setObservacao(orcamento.observacao ?? '');
    // Financeiro existente
    if (orcamento.formaPagRecId) setFormaPagRecId(orcamento.formaPagRecId);
    if (orcamento.qtdParcelas && orcamento.qtdParcelas > 0) setQtdParcelas(orcamento.qtdParcelas);
    if (orcamento.parcelas && orcamento.parcelas.length > 0) {
      setParcelas(
        orcamento.parcelas
          .slice()
          .sort((a, b) => a.nroParcela - b.nroParcela)
          .map((p) => ({
            numero:        p.nroParcela,
            formaPagRecId: p.formaPagRecId ?? '',
            dtVencimento:  p.dtVencimento.slice(0, 10),
            valor:         p.valor,
          })),
      );
    }
  }, [orcamento]);

  // ── Totalizadores ──────────────────────────────────────────────────────

  const totalBruto    = itens.reduce((s, i) => s + i.qtdMovimento * i.valorUnitario, 0);
  const totalDesconto = itens.reduce((s, i) => s + i.valorDesconto, 0);
  const totalAcrescimo = itens.reduce((s, i) => s + i.valorAcrescimo, 0);
  const totalLiquido  = totalBruto - totalDesconto + totalAcrescimo;

  // Em edição os itens são read-only; o total líquido para o financeiro vem do orçamento salvo.
  const totalLiquidoFinanceiro = isEditing ? (orcamento?.totalLiquido ?? 0) : totalLiquido;

  // ── Geração de parcelas (financeiro) ───────────────────────────────────
  const gerarParcelas = () => {
    if (totalLiquidoFinanceiro <= 0 || qtdParcelas < 1) return;
    const vlrParcela = Math.round((totalLiquidoFinanceiro / qtdParcelas) * 100) / 100;
    const novas: ParcelaPagamento[] = Array.from({ length: qtdParcelas }, (_, i) => {
      const dt = new Date(dtPrimeiroVenc);
      dt.setDate(dt.getDate() + i * diasParcela);
      const ultima = i === qtdParcelas - 1;
      return {
        numero:        i + 1,
        formaPagRecId: formaPagRecId,
        dtVencimento:  dt.toISOString().slice(0, 10),
        valor:         ultima
          ? Math.round((totalLiquidoFinanceiro - vlrParcela * (qtdParcelas - 1)) * 100) / 100
          : vlrParcela,
      };
    });
    setParcelas(novas);
  };

  // Monta o payload de parcelas para a API (undefined quando não há parcelas).
  const buildParcelasRequest = (): OrcamentoParcelaRequest[] | undefined => {
    if (parcelas.length === 0) return undefined;
    return parcelas.map((p) => ({
      nroParcela:   p.numero,
      dtVencimento: new Date(p.dtVencimento).toISOString(),
      valor:        p.valor,
      formaPagRecId: p.formaPagRecId !== '' ? Number(p.formaPagRecId) : undefined,
    }));
  };

  // ── Handlers de itens ──────────────────────────────────────────────────

  const updateItem = (key: number, campo: keyof ItemProduto, valor: string | number) => {
    setItens((prev) => prev.map((it) => it.key === key ? { ...it, [campo]: valor } : it));
  };

  const removeItem = (key: number) => {
    setItens((prev) => prev.filter((it) => it.key !== key));
  };

  const addItem = () => setItens((prev) => [...prev, ITEM_VAZIO(nextKey())]);

  // ── Converter em DAV ──────────────────────────────────────────────────

  const converterMutation = useMutation({
    mutationFn: async (orcId: number) => {
      const det = await orcamentoService.getById(orcId);
      return davService.create({
        vendedorId: det.vendedorId,
        dtEmissao: new Date().toISOString(),
        clienteId: det.clienteId,
        nomeRazao: det.nomeRazao ?? undefined,
        cpfCnpj: det.cpfCnpj ?? undefined,
        fone: det.fone ?? undefined,
        celular: det.celular ?? undefined,
        observacao: det.observacao ?? undefined,
        formaPagRecId: det.formaPagRecId ?? undefined,
        parcela: det.qtdParcelas && det.qtdParcelas > 0 ? det.qtdParcelas : undefined,
        produtos: det.produtos.map((p) => ({
          produtoId: p.produtoId,
          subProdutoId: p.subProdutoId,
          qtdMovimento: p.qtdMovimento,
          valorUnitario: p.valorUnitario,
          valorDesconto: p.valorDesconto,
          valorAcrescimo: p.valorAcrescimo,
        })),
        parcelas: det.parcelas && det.parcelas.length > 0
          ? det.parcelas.map((p) => ({
              nroParcela:    p.nroParcela,
              dtVencimento:  p.dtVencimento,
              valor:         p.valor,
              formaPagRecId: p.formaPagRecId,
            }))
          : undefined,
      });
    },
    onSuccess: (dav) => {
      queryClient.invalidateQueries({ queryKey: ['dav'] });
      queryClient.invalidateQueries({ queryKey: ['orcamento'] });
      toast.success('Orçamento convertido em Pedido de Venda com sucesso!');
      navigate(`/operacional/dav/${dav.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  // ── Salvar ─────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!vendedorId) throw new Error('Vendedor obrigatório');
      const produtosValidos = itens.filter((it) => it.produtoId !== '');
      if (produtosValidos.length === 0) throw new Error('Adicione pelo menos um produto');

      const request: CriarOrcamentoRequest = {
        vendedorId,
        dtEmissao: new Date(dtEmissao).toISOString(),
        dtValidade: new Date(dtValidade).toISOString(),
        dtPrevisao: new Date(dtPrevisao || dtValidade).toISOString(),
        clienteId:  clienteId ?? undefined,
        nomeRazao:  nomeRazao || undefined,
        cpfCnpj:    cpfCnpj   || undefined,
        fone:       fone       || undefined,
        celular:    celular    || undefined,
        observacao: observacao || undefined,
        produtos: produtosValidos.map<OrcamentoProdutoRequest>((it) => ({
          produtoId:     Number(it.produtoId),
          subProdutoId:  0,
          qtdMovimento:  it.qtdMovimento,
          valorUnitario: it.valorUnitario,
          valorDesconto: it.valorDesconto,
          valorAcrescimo: it.valorAcrescimo,
          observacao:    it.observacao || undefined,
        })),
        formaPagRecId: formaPagRecId !== '' ? Number(formaPagRecId) : undefined,
        qtdParcelas,
        diasParcela,
        parcelas: buildParcelasRequest(),
      };

      return orcamentoService.create(request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orcamento'] });
      toast.success('Orçamento salvo com sucesso');
      navigate(`/operacional/orcamentos/${data.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  // ── Atualizar (edição — cabeçalho) ─────────────────────────────────────
  // Os itens de um orçamento existente são exibidos em modo leitura; a edição
  // de cabeçalho (cliente, validade, contato, observação) é persistida via PUT.

  const updateMutation = useMutation({
    mutationFn: async () => {
      const request: AtualizarOrcamentoRequest = {
        clienteId:  clienteId ?? undefined,
        nomeRazao:  nomeRazao || undefined,
        cpfCnpj:    cpfCnpj   || undefined,
        fone:       fone       || undefined,
        celular:    celular    || undefined,
        observacao: observacao || undefined,
        dtValidade: new Date(dtValidade).toISOString(),
        formaPagRecId: formaPagRecId !== '' ? Number(formaPagRecId) : undefined,
        qtdParcelas,
        diasParcela,
        parcelas: buildParcelasRequest(),
      };
      return orcamentoService.update(Number(id), request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamento'] });
      queryClient.invalidateQueries({ queryKey: ['orcamento-detalhe', id] });
      toast.success('Orçamento atualizado com sucesso');
      navigate('/operacional/orcamentos');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const handleSave = () => (isEditing ? updateMutation : saveMutation).mutate();

  // ── Render ─────────────────────────────────────────────────────────────

  const isBaixado = orcamento?.isBaixada ?? false;

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/orcamentos')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? `Orçamento #${orcamento?.numeroOrcamento ?? '...'}` : 'Novo Orçamento'}
            </h1>
            {isBaixado && <Badge className="bg-green-500/10 text-green-500 mt-1">Aprovado</Badge>}
          </div>
        </div>

        <div className="flex gap-2">
          {isEditing && !isBaixado && (
            <Button
              variant="outline"
              className="gap-2"
              disabled={converterMutation.isPending}
              onClick={() => converterMutation.mutate(Number(id))}
            >
              <ArrowRightCircle className="h-4 w-4" />
              Converter em Pedido
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/operacional/orcamentos')}>
            Cancelar
          </Button>
          {!isBaixado && (
            <Button
              className="gap-2"
              onClick={handleSave}
              disabled={saveMutation.isPending || updateMutation.isPending}
            >
              <Save className="h-4 w-4" />
              Salvar
            </Button>
          )}
        </div>
      </div>

      {/* Abas */}
      <Tabs defaultValue="identificacao">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="identificacao" className="gap-2">
            <User className="h-4 w-4" />Identificação
          </TabsTrigger>
          <TabsTrigger value="produtos" className="gap-2">
            <Package className="h-4 w-4" />Produtos
          </TabsTrigger>
          <TabsTrigger value="financeiro" className="gap-2">
            <CreditCard className="h-4 w-4" />Financeiro
          </TabsTrigger>
          <TabsTrigger value="observacoes" className="gap-2">
            <ClipboardList className="h-4 w-4" />Observações
          </TabsTrigger>
        </TabsList>

        {/* ── Aba Identificação ─────────────────────────────────────────── */}
        <TabsContent value="identificacao">
          <Card>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Vendedor */}
              <div className="space-y-2">
                <Label>Vendedor <span className="text-destructive">*</span></Label>
                <VendedorSearchCombobox
                  value={vendedorId}
                  onChange={(val) => setVendedorId(val)}
                  placeholder="Buscar vendedor..."
                  disabled={isBaixado}
                />
              </div>

              {/* Datas */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Data de Emissão</Label>
                  <Input
                    type="date" value={dtEmissao}
                    onChange={(e) => setDtEmissao(e.target.value)}
                    disabled={isBaixado}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Validade</Label>
                  <Input
                    type="date" value={dtValidade}
                    onChange={(e) => setDtValidade(e.target.value)}
                    disabled={isBaixado}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Previsão</Label>
                  <Input
                    type="date" value={dtPrevisao}
                    onChange={(e) => setDtPrevisao(e.target.value)}
                    disabled={isBaixado}
                  />
                </div>
              </div>

              <Separator className="md:col-span-2" />

              {/* Cliente */}
              <div className="space-y-2">
                <Label>Cliente Cadastrado</Label>
                <PessoaSearchCombobox
                  value={clienteId}
                  onChange={(val) => setClienteId(val)}
                  placeholder="Buscar cliente..."
                  filter="cliente"
                  disabled={isBaixado}
                />
              </div>

              {/* Consumidor final */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome / Razão Social</Label>
                  <Input
                    value={nomeRazao}
                    onChange={(e) => setNomeRazao(e.target.value)}
                    placeholder="Nome do consumidor final"
                    disabled={isBaixado}
                  />
                </div>
                <div className="space-y-2">
                  <Label>CPF / CNPJ</Label>
                  <Input
                    value={cpfCnpj}
                    onChange={(e) => setCpfCnpj(e.target.value)}
                    placeholder="000.000.000-00"
                    disabled={isBaixado}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  value={fone}
                  onChange={(e) => setFone(e.target.value)}
                  placeholder="(00) 0000-0000"
                  disabled={isBaixado}
                />
              </div>
              <div className="space-y-2">
                <Label>Celular</Label>
                <Input
                  value={celular}
                  onChange={(e) => setCelular(e.target.value)}
                  placeholder="(00) 00000-0000"
                  disabled={isBaixado}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Aba Produtos ──────────────────────────────────────────────── */}
        <TabsContent value="produtos">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Itens do Orçamento</CardTitle>
                {!isEditing && (
                  <Button size="sm" variant="outline" className="gap-2" onClick={addItem}>
                    <Plus className="h-4 w-4" />Adicionar item
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                /* Orçamento existente: itens em modo leitura (a edição de itens
                   é feita criando um novo orçamento; aqui edita-se o cabeçalho). */
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-right">Qtd</TableHead>
                        <TableHead className="text-right">Vlr Unit.</TableHead>
                        <TableHead className="text-right">Desconto</TableHead>
                        <TableHead className="text-right">Acréscimo</TableHead>
                        <TableHead>Obs.</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(orcamento?.produtos ?? []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                            Nenhum item neste orçamento.
                          </TableCell>
                        </TableRow>
                      ) : (
                        (orcamento?.produtos ?? []).map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>{p.produtoDescricao ?? <ProdutoNome id={p.produtoId} />}</TableCell>
                            <TableCell className="text-right">{p.qtdMovimento}</TableCell>
                            <TableCell className="text-right">
                              {p.valorUnitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </TableCell>
                            <TableCell className="text-right">
                              {p.valorDesconto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </TableCell>
                            <TableCell className="text-right">
                              {p.valorAcrescimo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{p.observacao ?? '—'}</TableCell>
                            <TableCell className="text-right font-medium">
                              {p.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-right">Qtd</TableHead>
                          <TableHead className="text-right">Vlr Unit.</TableHead>
                          <TableHead className="text-right">Desconto</TableHead>
                          <TableHead className="text-right">Acréscimo</TableHead>
                          <TableHead>Obs.</TableHead>
                          <TableHead className="text-right">Subtotal</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {itens.map((item) => (
                          <ProdutoLinha
                            key={item.key}
                            item={item}
                            onChange={(campo, valor) => updateItem(item.key, campo, valor)}
                            onRemove={() => removeItem(item.key)}
                            showRemove={itens.length > 1}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Totalizadores */}
                  <div className="mt-4 flex justify-end">
                    <div className="w-72 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Bruto:</span>
                        <span>{totalBruto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                      </div>
                      <div className="flex justify-between text-destructive">
                        <span>Desconto:</span>
                        <span>- {totalDesconto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Acréscimo:</span>
                        <span>+ {totalAcrescimo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-bold text-base">
                        <span>Total Líquido:</span>
                        <span>{totalLiquido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Aba Financeiro ────────────────────────────────────────────── */}
        <TabsContent value="financeiro">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Condições de Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {isBaixado ? (
                /* Orçamento aprovado — financeiro em leitura. */
                parcelas.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-2">
                    Nenhuma condição de pagamento informada.
                  </p>
                ) : (
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Parcela</TableHead>
                          <TableHead>Forma de Pagamento</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parcelas.map((p) => (
                          <TableRow key={p.numero}>
                            <TableCell className="font-medium">{p.numero}/{parcelas.length}</TableCell>
                            <TableCell>
                              {formasPagamento.find((fp) => fp.id === p.formaPagRecId)?.descricao ?? '—'}
                            </TableCell>
                            <TableCell>{new Date(p.dtVencimento).toLocaleDateString('pt-BR')}</TableCell>
                            <TableCell className="text-right font-medium">
                              {p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )
              ) : (
                <>
                  {/* Condição de pagamento — preenche forma/parcelas/intervalo */}
                  <div className="space-y-1 max-w-xl">
                    <Label>Condição de Pagamento</Label>
                    <Select
                      value={condicaoPagamentoId !== '' ? String(condicaoPagamentoId) : '__none__'}
                      onValueChange={(v) => {
                        if (v === '__none__') { setCondicaoPagamentoId(''); return; }
                        const cid = Number(v);
                        setCondicaoPagamentoId(cid);
                        const cond = condicoesPagamento.find((c) => c.id === cid);
                        if (cond) {
                          if (cond.formaPagRecId) setFormaPagRecId(cond.formaPagRecId);
                          if (cond.quantidadeParcelas && cond.quantidadeParcelas > 0) setQtdParcelas(cond.quantidadeParcelas);
                          if (cond.diasMes != null && cond.diasMes > 0) setDiasParcela(cond.diasMes);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma condição (opcional)..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Nenhuma (informar manualmente) —</SelectItem>
                        {condicoesPagamento.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {(c.descricao ?? `Condição ${c.codigoCondicao}`)} — {c.quantidadeParcelas}x
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Preenche forma de pagamento, nº de parcelas e intervalo automaticamente.
                    </p>
                  </div>

                  {/* Forma + nº parcelas + dias + 1º vencimento */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-1 sm:col-span-2">
                      <Label>Forma de Pagamento</Label>
                      <Select
                        value={formaPagRecId !== '' ? String(formaPagRecId) : ''}
                        onValueChange={(v) => setFormaPagRecId(v ? Number(v) : '')}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {formasPagamento.map((fp) => (
                            <SelectItem key={fp.id} value={String(fp.id)}>
                              {fp.descricao}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Nº de Parcelas</Label>
                      <Input
                        type="number" min={1} max={99}
                        value={qtdParcelas}
                        onChange={(e) => setQtdParcelas(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Dias entre Parcelas</Label>
                      <Input
                        type="number" min={0}
                        value={diasParcela}
                        onChange={(e) => setDiasParcela(Number(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label>Data 1º Vencimento</Label>
                      <Input
                        type="date"
                        value={dtPrimeiroVenc}
                        onChange={(e) => setDtPrimeiroVenc(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Resumo + gerar parcelas */}
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 p-4 rounded-md bg-muted/40">
                    <div className="flex gap-8 text-sm">
                      <div>
                        <p className="text-muted-foreground">Valor Líquido</p>
                        <p className="font-semibold text-base text-emerald-600">
                          {totalLiquidoFinanceiro.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                      </div>
                      {qtdParcelas > 1 && totalLiquidoFinanceiro > 0 && (
                        <div>
                          <p className="text-muted-foreground">Valor por Parcela</p>
                          <p className="font-semibold text-base">
                            {(totalLiquidoFinanceiro / qtdParcelas).toLocaleString('pt-BR', {
                              style: 'currency', currency: 'BRL',
                            })}
                          </p>
                        </div>
                      )}
                    </div>
                    <Button type="button" variant="outline" onClick={gerarParcelas} className="gap-2">
                      <CreditCard className="h-4 w-4" />
                      Gerar Parcelas
                    </Button>
                  </div>

                  {/* Grid de parcelas */}
                  {parcelas.length > 0 && (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">Parcela</TableHead>
                            <TableHead>Forma de Pagamento</TableHead>
                            <TableHead>Vencimento</TableHead>
                            <TableHead className="text-right">Valor</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parcelas.map((p) => (
                            <TableRow key={p.numero}>
                              <TableCell className="font-medium">{p.numero}/{qtdParcelas}</TableCell>
                              <TableCell>
                                <Select
                                  value={p.formaPagRecId !== '' ? String(p.formaPagRecId) : ''}
                                  onValueChange={(v) =>
                                    setParcelas((prev) =>
                                      prev.map((x) =>
                                        x.numero === p.numero
                                          ? { ...x, formaPagRecId: v ? Number(v) : '' }
                                          : x,
                                      ),
                                    )
                                  }
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Selecione..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {formasPagamento.map((fp) => (
                                      <SelectItem key={fp.id} value={String(fp.id)}>
                                        {fp.descricao}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="date"
                                  value={p.dtVencimento}
                                  onChange={(e) =>
                                    setParcelas((prev) =>
                                      prev.map((x) =>
                                        x.numero === p.numero ? { ...x, dtVencimento: e.target.value } : x,
                                      ),
                                    )
                                  }
                                  className="h-8 w-36"
                                />
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Aba Observações ───────────────────────────────────────────── */}
        <TabsContent value="observacoes">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Observações gerais do orçamento..."
                  rows={8}
                  disabled={isBaixado}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rodapé de ações */}
      {!isBaixado && (
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => navigate('/operacional/orcamentos')}>
            Cancelar
          </Button>
          <Button className="gap-2" onClick={handleSave} disabled={saveMutation.isPending}>
            <Save className="h-4 w-4" />Salvar
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrcamentoForm;
